/**
 * Modelled engine inputs for reconstructed races (owner Q3): rpm / gear / throttle are derived from the MODELLED
 * speed, since such a bundle has no car data. A simple 7-speed gearbox with shift hysteresis, a 2011 V8 rpm range
 * (about 18,000 max) and throttle from the sign of the acceleration. Deterministic, DOM-free, stateful per car
 * only for the gear (hysteresis) and the previous speed sample. Illustrative, not a physical model.
 */

export const RPM_MAX = 18000;
export const RPM_IDLE = 4500;
/** road speed (km/h) at RPM_MAX in each of the 7 gears */
export const GEAR_TOP_KMH: readonly number[] = [80, 115, 150, 185, 225, 265, 315];
const UP = 0.97, DOWN = 0.88;
/** acceleration (m/s^2) above which the throttle is flat out / below which it is lifted; between, a cruise trickle */
const ACC_ON = 0.3, ACC_OFF = -0.3, THR_CRUISE = 35;
/** a seek or a stall gives a bogus derivative: keep the last one beyond this race-time step (s) */
const MAX_DT = 0.5;

export interface ModelledTelemetry { rpm: number; thr: number; gear: number }

export class ModelledEngine {
  private readonly gear = new Map<number, number>();
  private readonly prev = new Map<number, { t: number; v: number; a: number }>();

  /** Params for car `idx` at race time `t` (s) and modelled speed `speedKmh`. Call in time order per car. */
  update(idx: number, t: number, speedKmh: number): ModelledTelemetry {
    const p = this.prev.get(idx);
    let a = p?.a ?? 0;
    if (p && t - p.t > 1e-6 && t - p.t <= MAX_DT) a = (speedKmh - p.v) / 3.6 / (t - p.t);
    if (!p || Math.abs(t - p.t) > 1e-6) this.prev.set(idx, { t, v: speedKmh, a });
    let g = this.gear.get(idx) ?? 1;
    while (g < GEAR_TOP_KMH.length && speedKmh > (GEAR_TOP_KMH[g - 1] ?? 0) * UP) g++;
    while (g > 1 && speedKmh < (GEAR_TOP_KMH[g - 2] ?? 0) * DOWN) g--;
    this.gear.set(idx, g);
    const rpm = Math.min(RPM_MAX, Math.max(RPM_IDLE, (RPM_MAX * speedKmh) / (GEAR_TOP_KMH[g - 1] ?? 1)));
    const thr = a > ACC_ON ? 100 : a < ACC_OFF ? 0 : THR_CRUISE;
    return { rpm, thr, gear: g };
  }
}
